import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Droplets, Award, ArrowRight, Download, ExternalLink, Cpu,
  BarChart3, AlertTriangle, ChevronDown, ChevronUp, FileText,
  Activity, Compass, Database, Eye, Globe, Layers, ShieldCheck, Sparkles
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { Language } from '@/types';
import DroughtWatchV2Dashboard from '@/tools/droughtwatch/droughtwatch_v2_package/frontend/DroughtWatchV2Dashboard';

interface DroughtWatchPageProps {
  lang: Language;
}

const DroughtWatchPage: React.FC<DroughtWatchPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [heroTab, setHeroTab] = useState<'thermal' | 'progression' | 'hotspots'>('thermal');

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const t = {
    eyebrow: isAr
      ? 'أبحاث فكسل · مرصد DROUGHTWATCH V3'
      : 'VEXL RESEARCH · DROUGHTWATCH AI V3.0',
    heroTitle: isAr
      ? 'التنبؤ بالجفاف الزراعي قبل أن يلامس السطح.'
      : 'Predict drought before it reaches the surface.',
    heroDesc: isAr
      ? 'نموذج ذكاء مكاني-زماني متقدم يتنبأ بمسارات الجفاف الزراعي لـ 6 أشهر مسبقاً عبر 25,522 بكسلاً زراعياً في حوض الفيوم، مع حدود احتمالية رياضية غير قابلة للتقاطع لدعم التأمين البارامتري والائتمان الزراعي.'
      : 'An enterprise spatio-temporal Earth intelligence model delivering calibrated 180-day drought trajectories and non-crossing quantile risk bounds across 25,522 agricultural parcels.',
    btnLaunch: isAr ? 'أطلق المرصد التفاعلي' : 'Launch Observatory',
    btnPaper: isAr ? 'ورقة البحث المحكّمة (IEEE) ↗' : 'Read IEEE Paper ↗',
    btnDeck: isAr ? 'عرض الـ Pitch Deck الرسمي' : 'Pitch Deck',
    btnBenchmark: isAr ? 'سجل المقارنات المعيارية' : 'View Benchmarks',

    heroTabs: {
      thermal: isAr ? 'الخريطة الحرارية (250m)' : 'Thermal Stress (250m)',
      progression: isAr ? 'المسار الزمني (t+1 إلى t+6)' : '6-Month Trajectory',
      hotspots: isAr ? 'بؤر الخطر الحرج (P10 < 0.35)' : 'Hazard Alert Hotspots'
    },

    socialProof: isAr
      ? 'مرصد علمي معتمد حائز على جائزة الأمم المتحدة UNEP-Tongji 2026 ومبني على أرشيفات الفضاء العالمية'
      : 'Recognized by global environmental institutions and powered by planetary Earth observation archives',

    metrics: [
      {
        value: '6 Months',
        label: isAr ? 'أفق التنبؤ الاستباقي' : 'Forward Lead Time',
        sub: isAr ? 'تنبؤ متعدد الخطوات t+1 إلى t+6' : '180-day recursive horizon'
      },
      {
        value: '0.03 ms',
        label: isAr ? 'زمن الاستدلال لكل بكسل' : 'Per-Pixel Latency',
        sub: isAr ? 'استدلال فوري على وحدات CUDA' : 'CUDA TensorRT accelerated'
      },
      {
        value: '25,522',
        label: isAr ? 'بكسل زراعي مرصود' : 'Cropland Parcels',
        sub: isAr ? 'حوض الفيوم بالكامل بدقة 250m' : 'Full Faiyum basin at 250m'
      },
      {
        value: '-21.50%',
        label: isAr ? 'انخفاض في خطأ التنبؤ' : 'MAE Error Reduction',
        sub: isAr ? 'تفوق حاسم على نماذج الـ Climatology' : 'State-of-the-art over V1 baseline'
      }
    ],

    breakthroughs: [
      {
        num: '01',
        title: isAr
          ? 'مهلة استباقية لـ 180 يوماً تنهي عجز المراقبة اللحظية'
          : 'A 180-Day Runway: Overcoming the Reactive Monitoring Bottleneck',
        desc: isAr
          ? 'الجفاف الزراعي صدمة بطيئة التسلل تبدأ في استنزاف رطوبة الجذور العميقة قبل أن يجف الغطاء النباتي الظاهري بأسابيع. النظم الفضائية التقليدية تكتشف الضرر بعد موت المحصول بالفعل، بينما يمتص DroughtWatch الذاكرة الهيدرولوجية لـ 24 شهراً ليتنبأ بالجفاف قبل ظهوره على السطح بـ 6 أشهر.'
          : 'Agricultural drought is a slow-onset creeping hazard. Root-zone soil moisture depletions initiate irreversible necrosis weeks before spectral canopy loss becomes visible. Standard satellite monitoring reacts after crop loss has occurred. DroughtWatch models 24 months of subsurface hydrological memory to predict stress 6 months before surface symptoms emerge.'
      },
      {
        num: '02',
        title: isAr
          ? 'تكميم احتمالي رتيب يضمن عدم تقاطع منحنيات المخاطر'
          : 'Strictly Monotonic Uncertainty: Guaranteed P10 <= P50 <= P90 Bounds',
        desc: isAr
          ? 'نماذج الذكاء الاصطناعي الاحتمالية التقليدية تعاني من خطأ التكميم الشائع (Quantile Crossing) حيث يتنبأ النموذج بـ P10 أكبر من P50، مما يجعلها غير صالحة للاعتماد البنكي. ابتكر DroughtWatch V3 دالة إضافية رتيبة (Softplus Additive Head) مع خسارة Pinball غير متناظرة، لتوفير حدود مخاطر موثوقة رياضياً بنسبة 100% للتأمين البارامتري.'
          : 'Standard probabilistic neural networks suffer from quantile crossing, where anomalous sampling yields P10 > P50—an operational failure for financial underwriting. DroughtWatch V3 introduces a strictly monotonic additive Softplus head with asymmetric Pinball loss (alpha=2.0), mathematically guaranteeing valid non-crossing risk bounds for parametric micro-insurance.'
      },
      {
        num: '03',
        title: isAr
          ? 'نواة هجينة تدمج 9 قنوات فضائية ومناخية وسياقية'
          : '9-Channel Multi-Source Spatio-Temporal Core',
        desc: isAr
          ? 'لا يكتفي النموذج بمؤشر واحد، بل يدمج مصفوفة متكاملة تشمل انعكاسات MODIS الطيفية والحرارية، وبيانات هطول الأمطار من CHIRPS، ورطوبة التربة الجذرية من GLDAS، مع فلاتر الالتفاف المكاني (Spatial Lag Kernels 3x3) وتضمين الفصول الهارمونيكي لمنع الانقطاعات السنوية.'
          : 'Rather than relying on isolated indices, the engine ingests high-frequency MODIS NDVI/EVI and Land Surface Temperature, CHIRPS precipitation grids, GLDAS root-zone moisture dynamics, 3x3 spatial autoregressive convolutions, and continuous harmonic cyclical calendar encodings.'
      }
    ],

    observatory: {
      eyebrow: isAr ? 'المرصد التفاعلي المباشر' : 'Live Interactive Observatory',
      title: isAr ? 'محاكاة الاستدلال الجغرافي اللحظي' : 'Real-Time Spatio-Temporal Inference Engine',
      desc: isAr
        ? 'تفاعل مباشرة مع النموذج المنشور على وحدات الإنتاج. استعرض مسارات التنبؤ الـ 6 أشهر القادمة، واختبر سيناريوهات الصدمات المناخية عبر 25,522 بكسلاً زراعياً في الفيوم.'
        : 'Direct interface with the production model. Simulate climatic shock scenarios, inspect quantile envelopes (P10-P90), and observe spatial hazard evolution across Faiyum’s 25,522 cropland nodes.'
    },

    spatial: {
      eyebrow: isAr ? 'الخرائط المكانية والبؤر الحرجة' : 'Spatial Forecasts & Critical Clusters',
      title: isAr ? 'رصد بؤر الجفاف الحرجة لشهر مايو 2026' : 'May 2026 Severe Hazard Clusters & Progression',
      desc: isAr
        ? 'تُظهر نتائج النموذج الفعلي تركز الإجهاد المائي الحرج عند نهايات شبكات الري في طامية وإطسا، حيث يهبط احتمال P10 عن عتبة الخطر الشديد (0.35).'
        : 'Empirical model forecasts demonstrate spatial drought propagation concentrated at canal terminations in Tamiyyah and Itsa, where lower-bound P10 breaches the 0.35 severe drought threshold.'
    },

    benchmarks: {
      eyebrow: isAr ? 'النتائج التجريبية والمقارنات' : 'Peer-Reviewed Benchmarks',
      title: isAr ? 'المقارنة المعيارية العلمية (Table II)' : 'Rigorous Multi-Model Evaluation (Table II)',
      desc: isAr
        ? 'توثق الاختبارات التجريبية على بيانات خارج العينة (Test Set 2023-2025) تفوق الإصدار V3 عبر كافة الآفاق الزمنية مقارنة بنماذج السلاسل الزمنية الكلاسيكية والإصدارات السابقة.'
        : 'Out-of-sample empirical evaluation on 2023-2025 holdout sequences confirms V3’s superior accuracy, uncertainty calibration, and real-time efficiency.'
    },

    fintech: {
      eyebrow: isAr ? 'الجسر المالي والاقتصادي' : 'FinTech & Parametric Solvency Bridge',
      title: isAr ? 'AgroScore: تحويل التنبؤ المناخي إلى جدارة ائتمانية وتأمين فوري' : 'AgroScore: Climate-Indexed Credit Solvency & Parametric Payouts',
      desc: isAr
        ? 'يرتبط DroughtWatch مباشرة بمنظومة AgroScore لتحويل تنبؤات الجفاف إلى تقييمات ائتمانية رقمية (300 إلى 850)، وصرف تعويضات التأمين آلياً عبر العقود الذكية دون الحاجة للجان المعاينة الميدانية.'
        : 'DroughtWatch directly powers the AgroScore FinTech engine, converting P10 hazard forecasts into institutional credit scores (300-850) and automating parametric micro-insurance payouts via smart contracts.'
    },

    research: {
      eyebrow: isAr ? 'مركز الأبحاث والوثائق الرسمية' : 'Publications & Technical Assets',
      title: isAr ? 'الأوراق العلمية المحكّمة والعرض الرسمي' : 'Peer-Reviewed Literature & Master Pitch Deck',
      desc: isAr
        ? 'حمّل وثائق المشروع الرسمية، بما في ذلك ورقة البحث المكتملة بصيغة IEEE Transactions وعرض الهاكاثون نصف النهائي.'
        : 'Access full technical publications authored according to IEEE Transactions standards, along with the official semifinal pitch deck.'
    },

    faq: {
      eyebrow: isAr ? 'الأسئلة الشائعة' : 'Technical FAQ',
      title: isAr ? 'إجابات تقنية معمقة حول بنية النظام' : 'Frequently Asked Engineering Questions',
      items: [
        {
          q: isAr ? 'كيف يضمن النموذج V3 عدم تقاطع نسب التكميم (Quantile Crossing)؟' : 'How does DroughtWatch V3 mathematically prevent Quantile Crossing?',
          a: isAr
            ? 'في نماذج التكميم التقليدية، قد يتنبأ النموذج أحياناً بـ P10 أكبر من P50 نتيجة أخطاء العينة، وهو تناقض إحصائي غير منطقي. في V3، يستخدم النموذج دالة تفعيل رتيبة (Monotonic Additive Formulation): يتنبأ النموذج بـ P10 مباشرة، ثم يتنبأ بدلتا موجبة delta1 عبر دالة Softplus ليصبح P50 = P10 + Softplus(delta1)، وبالمثل P90 = P50 + Softplus(delta2)، مما يجعل تقاطع المنحنيات مستحيلاً رياضياً.'
            : 'Traditional quantile regression can produce anomalous crossings where P10 > P50. DroughtWatch V3 introduces a strictly monotonic additive formulation: P10 is predicted directly, while P50 = P10 + softplus(delta1) and P90 = P50 + softplus(delta2). Because softplus outputs strictly non-negative values, P10 <= P50 <= P90 is guaranteed across all samples.'
        },
        {
          q: isAr ? 'لماذا تم اختيار 24 شهراً كنافذة زمنية للمدخلات؟' : 'Why is a 24-month input sequence window necessary?',
          a: isAr
            ? 'الجفاف الزراعي والهيدرولوجي يتميز بظاهرة الذاكرة البطيئة (Hydrological Hysteresis). رطوبة التربة في الأعماق ومخزون المياه الجوفية يستجيبان لدورات جفاف مطولة تعود لعامين سابقين. نافذة الـ 24 شهراً تسمح لطبقات Bi-LSTM بالتقاط تعاقب الفصول المزدوج ومقارنة الموسم الحالي بسابقه.'
            : 'Agricultural drought features significant hydrological memory. Subsurface soil moisture and aquifer recharge dynamics respond to cumulative deficits over multi-year cycles. A 24-month lookback enables the Bi-LSTM to capture multi-seasonal recharge patterns and inter-annual climatic oscillations.'
        },
        {
          q: isAr ? 'كيف يتم تفعيل التأمين البارامتري الذكي عبر AgroScore؟' : 'How does the Parametric Insurance trigger operate?',
          a: isAr
            ? 'بدلاً من انتظار زيارة خبير المعاينة الميداني لشهور، يحدد العقد الذكي عتبة واضحة (مثلاً P10 < 0.35 لمدة شهرين متتاليين). بمجرد أن يطلق مرصد DroughtWatch هذا الإنذار، يتم إيداع التعويض المالي في المحفظة الإلكترونية للمزارع فوراً عبر واجهة البرمجة (API) للبنك الزراعي المصري.'
            : 'Traditional insurance requires weeks of manual loss appraisal. DroughtWatch enables zero-touch parametric insurance: smart contracts define an empirical trigger (e.g. forecasted P10 < 0.35). When the model validates this threshold, compensation is automatically disbursed to the farmer via the Agricultural Bank of Egypt API.'
        },
        {
          q: isAr ? 'هل يمكن تعميم النموذج على محافظات الصعيد ودلتا النيل؟' : 'Can the model be scaled to other Egyptian governorates and MENA regions?',
          a: isAr
            ? 'نعم. يعتمد خط الأنابيب البرمجي على بيانات فضائية عالمية موحدة ومفتوحة المصدر (MODIS, CHIRPS, GLDAS, ESA WorldCover). تم تدريب ومعايرة النموذج أولاً على حوض الفيوم المغلق كحالة اختبار معقدة ذات تضاريس متباينة، وهو مصمم ليعاد ضبطه (Fine-tuned) على أي حوض زراعي خلال ساعات.'
            : 'Yes. The data ingestion pipeline relies exclusively on global, open-access satellite feeds (MODIS, CHIRPS, GLDAS, ESA WorldCover). Faiyum was chosen as a closed-basin endorheic testbed. Transfer learning weights allow fine-tuning across Upper Egypt and the Nile Delta within hours.'
        }
      ]
    }
  };

  return (
    <PageTransition>
      <SEO
        title={isAr ? 'VEXL DroughtWatch AI V3.0 — مرصد الجفاف الذكي' : 'VEXL DroughtWatch AI V3.0 — Spatio-Temporal Earth Intelligence'}
        description="6-Month Spatio-Temporal Drought Forecasting with Monotonic Quantile Attention for Faiyum Governorate, Egypt. UNEP-Tongji 2026 Laureate."
        lang={lang}
      />

      <div className={`min-h-screen bg-[#05070A] text-[#EDF0F1] selection:bg-[#0E93A3]/30 selection:text-white relative overflow-hidden ${isAr ? 'font-arabic' : 'font-sans'}`} dir={isAr ? 'rtl' : 'ltr'}>
        {/* Clean Hairline Spatial Grid Backdrop */}
        <RasterGridBackdrop type="type-a" />

        {/* ─── 1. HERO SECTION (AUTHORITATIVE & CLEAN — OPENAI/CLAUDE CALIBER) ─── */}
        <section id="overview" className="pt-32 pb-20 px-6 relative z-10">
          <div className="max-w-[1280px] mx-auto text-center space-y-8">
            
            {/* Minimal Eyebrow */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#12161A] border border-[#262C31] text-[#9AA5AC] text-xs font-mono tracking-widest uppercase"
            >
              <span>{t.eyebrow}</span>
            </motion.div>

            {/* Powerful, Clear Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="text-4xl sm:text-6xl lg:text-[72px] font-medium text-[#EDF0F1] tracking-[-0.03em] leading-[1.08] max-w-4xl mx-auto"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.heroTitle}
            </motion.h1>

            {/* Authoritative Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-[#9AA5AC] text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto font-normal"
            >
              {t.heroDesc}
            </motion.p>

            {/* Two Clear, Decisive Actions */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap items-center justify-center gap-4 pt-2"
            >
              <button
                type="button"
                onClick={() => scrollToSection('observatory')}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white text-sm font-medium transition-all shadow-[0_0_25px_rgba(14,147,163,0.3)]"
              >
                <Droplets size={16} />
                <span>{t.btnLaunch}</span>
              </button>

              <a
                href="/research/papers/DroughtWatch_AI_V3_Paper.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-[#EDF0F1] text-sm font-medium transition-all"
              >
                <FileText size={15} className="text-[#9AA5AC]" />
                <span>{t.btnPaper}</span>
              </a>
            </motion.div>

            {/* ─── GRAND HERO STAGE (INTERACTIVE PRODUCT VIEWPORT) ─── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.6 }}
              className="pt-10 max-w-[1180px] mx-auto"
            >
              <div className="rounded-2xl border border-[#262C31] bg-[#0E1217] shadow-[0_30px_90px_rgba(0,0,0,0.85)] overflow-hidden">
                {/* Modern Window Header */}
                <div className="flex flex-wrap items-center justify-between px-5 py-3.5 bg-[#12161A] border-b border-[#262C31] gap-4">
                  {/* Window Controls */}
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <span className="text-xs font-mono text-[#9AA5AC] pl-3 border-l border-[#262C31] hidden sm:inline">
                      DroughtWatch V3 · Observatory Viewport
                    </span>
                  </div>

                  {/* Interactive Viewport Tabs */}
                  <div className="inline-flex items-center rounded-full bg-[#05070A] p-1 border border-[#262C31]">
                    <button
                      type="button"
                      onClick={() => setHeroTab('thermal')}
                      className={`px-3 py-1 rounded-full text-xs font-mono transition-all ${
                        heroTab === 'thermal'
                          ? 'bg-[#171C21] text-[#EDF0F1] border border-[#39424A] shadow-sm'
                          : 'text-[#9AA5AC] hover:text-[#EDF0F1]'
                      }`}
                    >
                      {t.heroTabs.thermal}
                    </button>
                    <button
                      type="button"
                      onClick={() => setHeroTab('progression')}
                      className={`px-3 py-1 rounded-full text-xs font-mono transition-all ${
                        heroTab === 'progression'
                          ? 'bg-[#171C21] text-[#EDF0F1] border border-[#39424A] shadow-sm'
                          : 'text-[#9AA5AC] hover:text-[#EDF0F1]'
                      }`}
                    >
                      {t.heroTabs.progression}
                    </button>
                    <button
                      type="button"
                      onClick={() => setHeroTab('hotspots')}
                      className={`px-3 py-1 rounded-full text-xs font-mono transition-all ${
                        heroTab === 'hotspots'
                          ? 'bg-[#171C21] text-[#EDF0F1] border border-[#39424A] shadow-sm'
                          : 'text-[#9AA5AC] hover:text-[#EDF0F1]'
                      }`}
                    >
                      {t.heroTabs.hotspots}
                    </button>
                  </div>

                  {/* Live Status Pill */}
                  <div className="flex items-center gap-2 text-xs font-mono text-[#9AA5AC]">
                    <span className="w-2 h-2 rounded-full bg-[#0E93A3] animate-pulse" />
                    <span className="text-[#EDF0F1]">Production Online</span>
                  </div>
                </div>

                {/* Stage Canvas (Image / View Switcher) */}
                <div className="relative aspect-[16/9] sm:aspect-[21/9] lg:aspect-[2.2/1] bg-[#05070A] overflow-hidden flex items-center justify-center p-2 sm:p-4">
                  <AnimatePresence mode="wait">
                    {heroTab === 'thermal' && (
                      <motion.div
                        key="thermal"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-full h-full relative flex items-center justify-center"
                      >
                        <img
                          src="/images/products/drought.webp"
                          alt="DroughtWatch AI Thermal Stress Map"
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </motion.div>
                    )}

                    {heroTab === 'progression' && (
                      <motion.div
                        key="progression"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-full h-full relative flex items-center justify-center"
                      >
                        <img
                          src="/fig2_spatial_forecast_6months.png"
                          alt="6-Month Spatial Progression Map"
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </motion.div>
                    )}

                    {heroTab === 'hotspots' && (
                      <motion.div
                        key="hotspots"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-full h-full relative flex items-center justify-center"
                      >
                        <img
                          src="/fig3_drought_risk_alert_hotspots.png"
                          alt="May 2026 Severe Hazard Clusters"
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Stage Bottom Metadata Ticker (Minimalist) */}
                <div className="px-6 py-3 bg-[#0B0F12] border-t border-[#262C31] flex flex-wrap items-center justify-between text-xs font-mono text-[#9AA5AC] gap-4" style={{ fontFeatureSettings: '"tnum"' }}>
                  <div className="flex items-center gap-6 flex-wrap">
                    <span>Model: <strong className="text-[#EDF0F1]">Bi-LSTM + Multi-Head Attention</strong></span>
                    <span>Grid: <strong className="text-[#EDF0F1]">250m Resolution</strong></span>
                    <span>Quantiles: <strong className="text-[#EDF0F1]">P10 &le; P50 &le; P90</strong></span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span>Inference: <strong className="text-[#0E93A3]">0.03 ms / node</strong></span>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </section>

        {/* ─── 2. INSTITUTIONAL SOCIAL PROOF RIBBON ─────────────────── */}
        <section className="py-12 px-6 border-y border-[#262C31] bg-[#070A0E]">
          <div className="max-w-[1280px] mx-auto text-center space-y-6">
            <span className="text-xs font-mono text-[#66707A] uppercase tracking-[0.2em] block">
              {t.socialProof}
            </span>
            <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-14 text-sm font-mono text-[#9AA5AC]">
              <div className="flex items-center gap-2 hover:text-[#EDF0F1] transition-colors">
                <Award size={16} className="text-[#0E93A3]" />
                <span>UNEP-Tongji Laureate</span>
              </div>
              <div className="flex items-center gap-2 hover:text-[#EDF0F1] transition-colors">
                <Globe size={16} className="text-[#0E93A3]" />
                <span>Cairo University AI Nexus</span>
              </div>
              <div className="flex items-center gap-2 hover:text-[#EDF0F1] transition-colors">
                <Layers size={16} className="text-[#0E93A3]" />
                <span>ESA Sentinel-2 MSI</span>
              </div>
              <div className="flex items-center gap-2 hover:text-[#EDF0F1] transition-colors">
                <Database size={16} className="text-[#0E93A3]" />
                <span>NASA MODIS & GLDAS</span>
              </div>
              <div className="flex items-center gap-2 hover:text-[#EDF0F1] transition-colors">
                <ShieldCheck size={16} className="text-[#0E93A3]" />
                <span>Agricultural Bank of Egypt</span>
              </div>
            </div>
          </div>
        </section>

        {/* ─── 3. FOUR QUANTITATIVE PROOF POINTS ────────────────────── */}
        <section className="py-20 px-6 max-w-[1280px] mx-auto border-b border-[#262C31]">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
            {t.metrics.map((m, i) => (
              <div key={i} className="space-y-2 border-l border-[#262C31] pl-6">
                <span
                  className="text-3xl sm:text-4xl lg:text-5xl font-medium font-mono text-[#EDF0F1] tracking-tight block"
                  style={{ fontFeatureSettings: '"tnum"' }}
                >
                  {m.value}
                </span>
                <span className="text-sm font-medium text-[#EDF0F1] block">
                  {m.label}
                </span>
                <span className="text-xs text-[#9AA5AC] font-mono block" style={{ fontFeatureSettings: '"tnum"' }}>
                  {m.sub}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ─── 4. THREE EDITORIAL BREAKTHROUGHS (APPLE/STRIPE STYLE) ── */}
        <section className="py-24 px-6 max-w-[1280px] mx-auto border-b border-[#262C31]">
          <div className="max-w-2xl mb-16 space-y-3">
            <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-widest block">
              {isAr ? 'الابتكارات العلمية الأساسية' : 'Core Scientific Innovations'}
            </span>
            <h2
              className="text-3xl sm:text-4xl font-medium text-[#EDF0F1] tracking-tight leading-tight"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {isAr
                ? 'كيف يعيد DroughtWatch تعريف التنبؤ بالمخاطر المناخية'
                : 'How DroughtWatch fundamentally redefines climate hazard intelligence'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {t.breakthroughs.map((item, i) => (
              <div key={i} className="space-y-4">
                <span className="text-xs font-mono text-[#0E93A3] font-medium block">
                  {item.num}
                </span>
                <h3
                  className="text-xl font-medium text-[#EDF0F1] tracking-tight leading-snug"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-sm text-[#9AA5AC] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── 5. NEURAL ARCHITECTURE DIAGRAM ───────────────────────── */}
        <section className="py-24 px-6 max-w-[1280px] mx-auto border-b border-[#262C31]">
          <div className="max-w-2xl mb-12 space-y-3">
            <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-widest block">
              {isAr ? 'المعمارية العصبية V3' : 'Neural Pipeline Architecture'}
            </span>
            <h2
              className="text-3xl sm:text-4xl font-medium text-[#EDF0F1] tracking-tight leading-tight"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {isAr
                ? 'بنية هجينة تجمع بين Conv1D والـ Bi-LSTM والـ Attention والتكميم الرتيب'
                : 'Hybrid 1D-CNN + Stacked Bi-LSTM + Multi-Head Attention + Monotonic Head'}
            </h2>
            <p className="text-sm text-[#9AA5AC] leading-relaxed">
              {isAr
                ? 'توثيق البنية العصبية الكاملة المنشورة في ورقة أبحاث IEEE Transactions. تمتص المعمارية 24 شهراً من المدخلات لاستخراج التوقعات المستقبلية دون أي تداخل في سيناريوهات عدم اليقين.'
                : 'Engineered to process 24-month sequences of 9-channel Earth observation tensors. Multi-Head Attention selectively weights high-vulnerability seasonal stress points while the strictly monotonic head enforces P10 <= P50 <= P90.'}
            </p>
          </div>

          <div className="rounded-2xl border border-[#262C31] bg-[#0E1217] p-6 sm:p-10 shadow-2xl">
            <img
              src="/droughtwatch_v3_architecture.png"
              alt="DroughtWatch V3 Neural Architecture Diagram"
              className="w-full h-auto max-h-[520px] object-contain rounded-xl mx-auto"
            />
          </div>
        </section>

        {/* ─── 6. LIVE PRODUCTION OBSERVATORY (FULL WIDTH TOOL) ──────── */}
        <section id="observatory" className="py-24 px-6 max-w-[1360px] mx-auto border-b border-[#262C31]">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
            <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-widest block">
              {t.observatory.eyebrow}
            </span>
            <h2
              className="text-3xl sm:text-4xl font-medium text-[#EDF0F1] tracking-tight"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.observatory.title}
            </h2>
            <p className="text-sm text-[#9AA5AC] leading-relaxed">
              {t.observatory.desc}
            </p>
          </div>

          <div className="rounded-2xl border border-[#262C31] bg-[#0B0F12] shadow-2xl overflow-hidden">
            {/* Minimalist Top Terminal Chrome */}
            <div className="flex items-center justify-between px-6 py-3.5 bg-[#12161A] border-b border-[#262C31]">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                <span className="text-xs font-mono text-[#EDF0F1] uppercase tracking-wider pl-3 border-l border-[#262C31]">
                  Production Inference // Faiyum 25,522 Cropland Nodes
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs font-mono text-[#0E93A3]">
                <span className="w-2 h-2 rounded-full bg-[#0E93A3] animate-pulse" />
                <span>CUDA Engine Active</span>
              </div>
            </div>

            <div className="p-3 sm:p-6 bg-[#05070A]">
              <DroughtWatchV2Dashboard />
            </div>
          </div>
        </section>

        {/* ─── 7. EMPIRICAL BENCHMARKS (TABLE II) ───────────────────── */}
        <section id="benchmarks" className="py-24 px-6 max-w-[1280px] mx-auto border-b border-[#262C31]">
          <div className="max-w-2xl mb-12 space-y-3">
            <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-widest block">
              {t.benchmarks.eyebrow}
            </span>
            <h2
              className="text-3xl sm:text-4xl font-medium text-[#EDF0F1] tracking-tight leading-tight"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.benchmarks.title}
            </h2>
            <p className="text-sm text-[#9AA5AC] leading-relaxed">
              {t.benchmarks.desc}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center mb-10">
            {/* Fig 1: Evolution Chart */}
            <div className="lg:col-span-1 p-6 rounded-2xl bg-[#0E1217] border border-[#262C31] space-y-4">
              <span className="text-xs font-mono text-[#EDF0F1] uppercase tracking-wider block">
                Fig. 1: Accuracy & Latency Evolution
              </span>
              <img
                src="/fig1_benchmark_evolution_v1_v2_v3.png"
                alt="Benchmark Evolution V1 vs V2 vs V3"
                className="w-full h-auto object-contain rounded-lg bg-[#05070A] p-2"
              />
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'انخفاض مستمر في الخطأ MAE وتسارع الاستدلال إلى 0.03 مللي ثانية لكل بكسل.'
                  : 'Monotonic predictive gains alongside a 12.8x speedup in per-pixel inference latency.'}
              </p>
            </div>

            {/* Table II Container */}
            <div className="lg:col-span-2 rounded-2xl border border-[#262C31] bg-[#0E1217] overflow-x-auto">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-[#262C31] bg-[#12161A] text-[#9AA5AC] font-mono text-[11px] uppercase tracking-wider">
                    <th className="p-4">{isAr ? 'النموذج' : 'Model Architecture'}</th>
                    <th className="p-4">MAE</th>
                    <th className="p-4">RMSE</th>
                    <th className="p-4">{isAr ? 'زمن الاستدلال' : 'Latency'}</th>
                    <th className="p-4">{isAr ? 'نسبة التحسن' : 'Gain vs Baseline'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262C31] text-[#EDF0F1] font-mono" style={{ fontFeatureSettings: '"tnum"' }}>
                  <tr className="hover:bg-white/[0.02] transition-colors">
                    <td className="p-4 font-sans font-normal text-[#9AA5AC]">
                      Historical Climatology (Harmonic Baseline)
                    </td>
                    <td className="p-4 text-[#9AA5AC]">0.0572</td>
                    <td className="p-4 text-[#9AA5AC]">0.0741</td>
                    <td className="p-4 text-[#66707A]">&lt; 0.01 ms</td>
                    <td className="p-4 text-[#66707A]">Baseline</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02] transition-colors">
                    <td className="p-4 font-sans font-medium text-[#EDF0F1]">
                      DroughtWatch V1 (Univariate Bi-LSTM)
                    </td>
                    <td className="p-4">0.0468</td>
                    <td className="p-4">0.0612</td>
                    <td className="p-4">0.42 ms</td>
                    <td className="p-4 text-[#639922]">-18.18%</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02] transition-colors">
                    <td className="p-4 font-sans font-medium text-[#EDF0F1]">
                      DroughtWatch V2 (Multivariate Bi-LSTM)
                    </td>
                    <td className="p-4">0.0413</td>
                    <td className="p-4">0.0536</td>
                    <td className="p-4">0.38 ms</td>
                    <td className="p-4 text-[#639922]">-27.80%</td>
                  </tr>
                  <tr className="bg-[#0E93A3]/10 hover:bg-[#0E93A3]/15 transition-colors border-l-2 border-[#0E93A3]">
                    <td className="p-4 font-sans font-medium text-white flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3]" />
                      <span>DroughtWatch V3 (Spatio-Temporal Quantile)</span>
                    </td>
                    <td className="p-4 text-[#0E93A3] font-bold">0.0367</td>
                    <td className="p-4 text-[#0E93A3] font-bold">0.0471</td>
                    <td className="p-4 text-[#0E93A3] font-bold">0.03 ms</td>
                    <td className="p-4 text-[#0E93A3] font-bold">-21.50% (vs V1)</td>
                  </tr>
                </tbody>
              </table>

              <div className="p-4 bg-[#12161A] border-t border-[#262C31] flex flex-wrap items-center justify-between gap-4 text-xs font-mono text-[#9AA5AC]">
                <div className="flex items-center gap-4" style={{ fontFeatureSettings: '"tnum"' }}>
                  <span>PICP (Coverage): <strong className="text-white">89.2%</strong> (Nominal 90%)</span>
                  <span>MPIW (Sharpness): <strong className="text-white">0.084</strong></span>
                </div>
                <Link
                  to="/droughtwatch/benchmark"
                  className="text-[#0E93A3] hover:text-[#075C68] flex items-center gap-1.5 font-medium transition-colors"
                >
                  <span>{isAr ? 'استعراض صفحة المقارنات التفصيلية' : 'Full Benchmark Suite'}</span>
                  <ArrowRight size={13} className={isAr ? 'rotate-180' : ''} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ─── 8. FINTECH & AGROSCORE UNDERWRITING BRIDGE ─────────────── */}
        <section className="py-24 px-6 max-w-[1280px] mx-auto border-b border-[#262C31]">
          <div className="rounded-2xl border border-[#262C31] bg-[#0E1217] p-8 sm:p-14 space-y-8">
            <div className="max-w-2xl space-y-3">
              <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-widest block">
                {t.fintech.eyebrow}
              </span>
              <h2
                className="text-3xl sm:text-4xl font-medium text-[#EDF0F1] tracking-tight leading-tight"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {t.fintech.title}
              </h2>
              <p className="text-sm text-[#9AA5AC] leading-relaxed">
                {t.fintech.desc}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-[#12161A] border border-[#262C31] space-y-2">
                <span className="text-xs font-mono text-[#0E93A3] font-medium block">
                  300 - 850 INSTITUTIONAL SCORE
                </span>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {isAr
                    ? 'تقييم جدارة ائتمانية رقمي يربط بين تاريخ استقرار المحاصيل لـ 24 عاماً وتوقعات الجفاف المستقبلية.'
                    : 'Quantitative rating integrating 24-year satellite yield stability with forward-looking P10 hazard probability.'}
                </p>
              </div>

              <div className="p-6 rounded-xl bg-[#12161A] border border-[#262C31] space-y-2">
                <span className="text-xs font-mono text-[#0E93A3] font-medium block">
                  BANK API UNDERWRITING
                </span>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {isAr
                    ? 'واجهة برمجة B2B للمصارف الزراعية لتسعير القروض وتجنب التعثر الجماعي قبل وقوع الصدمات.'
                    : 'B2B pay-per-score API integrating directly with core banking workflows to prevent default contagion.'}
                </p>
              </div>

              <div className="p-6 rounded-xl bg-[#12161A] border border-[#262C31] space-y-2">
                <span className="text-xs font-mono text-[#0E93A3] font-medium block">
                  ZERO-TOUCH PARAMETRIC INDEMNITY
                </span>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {isAr
                    ? 'صرف تعويضات التأمين فورياً عبر العقود الذكية بمجرد تحقق شرط هبوط P10 عن العتبة.'
                    : 'Smart contracts trigger automated indemnity payments without field appraisal delays.'}
                </p>
              </div>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/products/agroscore/workspace')}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white text-xs font-mono uppercase tracking-wider font-medium shadow-[0_0_20px_rgba(14,147,163,0.3)] transition-all"
              >
                <Activity size={14} />
                <span>{isAr ? 'دخول بوابة AgroScore' : 'Open AgroScore Portal'}</span>
              </button>

              <a
                href="/AgroScore_Pitch_Deck.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-[#262C31] text-[#EDF0F1] text-xs font-mono uppercase tracking-wider font-medium transition-all"
              >
                <Eye size={14} className="text-[#9AA5AC]" />
                <span>{isAr ? 'استعراض الشرائح التفاعلية (HTML)' : 'Interactive Slide Viewer'}</span>
              </a>
            </div>
          </div>
        </section>

        {/* ─── 9. TECHNICAL & ACADEMIC ASSETS HUB ─────────────────────── */}
        <section id="research" className="py-24 px-6 max-w-[1280px] mx-auto border-b border-[#262C31]">
          <div className="max-w-2xl mb-12 space-y-3">
            <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-widest block">
              {t.research.eyebrow}
            </span>
            <h2
              className="text-3xl sm:text-4xl font-medium text-[#EDF0F1] tracking-tight leading-tight"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.research.title}
            </h2>
            <p className="text-sm text-[#9AA5AC] leading-relaxed">
              {t.research.desc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1: V3 Paper */}
            <div className="p-6 rounded-2xl bg-[#0E1217] border border-[#262C31] flex flex-col justify-between hover:border-[#39424A] transition-all space-y-6">
              <div className="space-y-3">
                <span className="text-xs font-mono text-[#0E93A3] block">
                  IEEE TRANSACTIONS (2026)
                </span>
                <h3 className="text-base font-medium text-[#EDF0F1]">
                  DroughtWatch AI V3 Paper
                </h3>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {isAr
                    ? 'الورقة البحثية الرسمية الكاملة (10 صفحات) بتنسيق IEEE المحكّم مع المعادلات والجداول.'
                    : 'Complete publication-ready 10-page paper adhering strictly to IEEE Transactions standards.'}
                </p>
              </div>
              <div>
                <a
                  href="/research/papers/DroughtWatch_AI_V3_Paper.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white font-medium text-xs font-mono uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)]"
                >
                  <Download size={13} />
                  <span>{isAr ? 'تحميل ورقة V3 (PDF)' : 'Download V3 Paper'}</span>
                </a>
              </div>
            </div>

            {/* Card 2: AgroScore Pitch Deck */}
            <div className="p-6 rounded-2xl bg-[#0E1217] border border-[#262C31] flex flex-col justify-between hover:border-[#39424A] transition-all space-y-6">
              <div className="space-y-3">
                <span className="text-xs font-mono text-[#0E93A3] block">
                  CU AI NEXUS 2026
                </span>
                <h3 className="text-base font-medium text-[#EDF0F1]">
                  AgroScore Master Deck
                </h3>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {isAr
                    ? 'العرض التقديمي الشامل لنصف النهائي (12 شريحة عريضة 16:9) المصمم للجنة التحكيم.'
                    : '12-slide institutional pitch deck formatted for hackathon semifinal jury and VC evaluation.'}
                </p>
              </div>
              <div>
                <a
                  href="/AgroScore_Pitch_Deck.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white font-medium text-xs font-mono uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)]"
                >
                  <Download size={13} />
                  <span>{isAr ? 'تحميل الـ Deck (PDF)' : 'Download Deck PDF'}</span>
                </a>
              </div>
            </div>

            {/* Card 3: V2 & V1 Papers */}
            <div className="p-6 rounded-2xl bg-[#0E1217] border border-[#262C31] flex flex-col justify-between hover:border-[#39424A] transition-all space-y-6">
              <div className="space-y-3">
                <span className="text-xs font-mono text-[#9AA5AC] block">
                  ARCHIVAL REPOSITORIES
                </span>
                <h3 className="text-base font-medium text-[#EDF0F1]">
                  DroughtWatch V1 & V2
                </h3>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {isAr
                    ? 'الأوراق الأكاديمية للإصدارين السابقين لتوثيق التسلسل العلمي وتطور المنظومة.'
                    : 'Archival peer-reviewed literature documenting the developmental progression from V1 to V2.'}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href="/research/papers/DroughtWatch_AI_V2_Paper.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 py-2 px-3 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-[#262C31] text-[#EDF0F1] text-xs font-mono uppercase tracking-wider transition-all"
                >
                  <ExternalLink size={12} />
                  <span>{isAr ? 'ورقة V2 (PDF)' : 'Read V2 Paper'}</span>
                </a>
                <a
                  href="/research/papers/DroughtWatch_AI_V1_Paper.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 py-2 px-3 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-[#262C31] text-[#EDF0F1] text-xs font-mono uppercase tracking-wider transition-all"
                >
                  <ExternalLink size={12} />
                  <span>{isAr ? 'ورقة V1 (PDF)' : 'Read V1 Paper'}</span>
                </a>
              </div>
            </div>

            {/* Card 4: Model Artifacts */}
            <div className="p-6 rounded-2xl bg-[#0E1217] border border-[#262C31] flex flex-col justify-between hover:border-[#39424A] transition-all space-y-6">
              <div className="space-y-3">
                <span className="text-xs font-mono text-[#0E93A3] block">
                  PRODUCTION HARNESS
                </span>
                <h3 className="text-base font-medium text-[#EDF0F1]">
                  Model Weights (.keras)
                </h3>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {isAr
                    ? 'أوزان النموذج المدربة الجاهزة للإنتاج، وملفات المعايرة (scaler_v3)، وسكربتات الاختبار والتنفيذ.'
                    : 'Trained model weights (.keras), production scalers, and verification test suites for reproducible deployment.'}
                </p>
              </div>
              <div>
                <Link
                  to="/droughtwatch/benchmark"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-[#262C31] text-[#EDF0F1] text-xs font-mono uppercase tracking-wider transition-all"
                >
                  <Activity size={13} className="text-[#0E93A3]" />
                  <span>{isAr ? 'فحص الأكواد والنتائج' : 'Inspect Code & Tests'}</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ─── 10. SYSTEM SPECS & FAQ ────────────────────────────────── */}
        <section className="py-24 px-6 max-w-4xl mx-auto">
          <div className="space-y-3 text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-widest block">
              {t.faq.eyebrow}
            </span>
            <h2
              className="text-3xl sm:text-4xl font-medium text-[#EDF0F1] tracking-tight leading-tight"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.faq.title}
            </h2>
          </div>

          <div className="space-y-4">
            {t.faq.items.map((item, i) => (
              <div
                key={i}
                className="rounded-xl border border-[#262C31] bg-[#0E1217] overflow-hidden transition-colors"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(i)}
                  className="w-full p-6 text-left flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
                >
                  <span className="font-medium text-base text-[#EDF0F1]">
                    {item.q}
                  </span>
                  <div className="w-7 h-7 rounded-full bg-[#12161A] border border-[#262C31] flex items-center justify-center flex-shrink-0 text-[#9AA5AC]">
                    {activeFaq === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </button>
                <AnimatePresence>
                  {activeFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-6 pb-6 pt-1 text-[#9AA5AC] text-sm leading-relaxed border-t border-[#262C31]/50"
                    >
                      {item.a}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default DroughtWatchPage;
